export type PageSizeFormat = 'A3' | 'A4' | 'A5' | 'Letter' | 'Legal' | 'Tabloid' | 'Custom';
export type AspectRatio = '16:9' | '4:3' | 'Custom';
export type RenderType = 'document' | 'presentation' | 'webpage';

export interface SizeOption {
  value: string;
  label: string;
}

export interface PageDimensions {
  width: number;
  height: number;
}

export interface PageSizeDefinition {
  mm: PageDimensions;
  px: PageDimensions;
  in: PageDimensions;
}

export const PAGE_SIZES: Record<Exclude<PageSizeFormat, 'Custom'>, PageSizeDefinition> = {
  A3: {
    mm: { width: 297, height: 420 },
    px: { width: 1123, height: 1588 },
    in: { width: 11.69, height: 16.54 },
  },
  A4: {
    mm: { width: 210, height: 297 },
    px: { width: 794, height: 1123 },
    in: { width: 8.27, height: 11.69 },
  },
  A5: {
    mm: { width: 148, height: 210 },
    px: { width: 559, height: 794 },
    in: { width: 5.83, height: 8.27 },
  },
  Letter: {
    mm: { width: 216, height: 279 },
    px: { width: 816, height: 1056 },
    in: { width: 8.5, height: 11 },
  },
  Legal: {
    mm: { width: 216, height: 356 },
    px: { width: 816, height: 1344 },
    in: { width: 8.5, height: 14 },
  },
  Tabloid: {
    mm: { width: 279, height: 432 },
    px: { width: 1056, height: 1632 },
    in: { width: 11, height: 17 },
  },
} as const;

export const ASPECT_RATIOS: Record<Exclude<AspectRatio, 'Custom'>, PageDimensions> = {
  '16:9': { width: 1024, height: 576 },
  '4:3': { width: 1024, height: 768 },
} as const;

const DOCUMENT_SIZE_KEYS = Object.keys(PAGE_SIZES) as Array<Exclude<PageSizeFormat, 'Custom'>>;
const PRESENTATION_RATIO_KEYS = Object.keys(ASPECT_RATIOS) as Array<Exclude<AspectRatio, 'Custom'>>;

export const DOC_SIZE_OPTIONS_BY_RENDER_TYPE: Record<RenderType, SizeOption[]> = {
  document: DOCUMENT_SIZE_KEYS.map((size) => ({ value: size, label: size })),
  presentation: PRESENTATION_RATIO_KEYS.map((ratio) => ({ value: ratio, label: ratio })),
  webpage: [],
};

export const DEFAULT_DOC_SIZE_BY_RENDER_TYPE: Record<RenderType, string> = {
  document: 'A4',
  presentation: '16:9',
  webpage: '',
};

export const DEFAULT_DIMENSIONS = {
  document: { width: 980, height: 1050 },
  presentation: { width: 1130, height: 450 },
} as const;

export const PAGE_SIZE_LIMITS = {
  minWidth: 400,
  maxWidth: 1600,
  minHeight: 400,
  maxHeight: 2400,
} as const;

export const pxToMm = (px: number, dpi = 96): number => Math.round((px * 25.4) / dpi);
export const pxToIn = (px: number, dpi = 96): number => Math.round((px / dpi) * 100) / 100;

export function getDocumentDimensions(
  format: PageSizeFormat,
  customWidth?: number,
  customHeight?: number,
): PageDimensions {
  if (format === 'Custom') {
    return {
      width: customWidth || DEFAULT_DIMENSIONS.document.width,
      height: customHeight || DEFAULT_DIMENSIONS.document.height,
    };
  }
  return PAGE_SIZES[format].px;
}

export function getPresentationDimensions(
  aspectRatio: AspectRatio,
  customWidth?: number,
  customHeight?: number,
): PageDimensions {
  if (aspectRatio === 'Custom') {
    return {
      width: customWidth || DEFAULT_DIMENSIONS.presentation.width,
      height: customHeight || DEFAULT_DIMENSIONS.presentation.height,
    };
  }
  return ASPECT_RATIOS[aspectRatio];
}

export function validateDimensions(
  width: number,
  height: number,
): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (width < PAGE_SIZE_LIMITS.minWidth || width > PAGE_SIZE_LIMITS.maxWidth) {
    errors.push(
      `Width must be between ${PAGE_SIZE_LIMITS.minWidth}-${PAGE_SIZE_LIMITS.maxWidth}px`,
    );
  }
  if (height < PAGE_SIZE_LIMITS.minHeight || height > PAGE_SIZE_LIMITS.maxHeight) {
    errors.push(
      `Height must be between ${PAGE_SIZE_LIMITS.minHeight}-${PAGE_SIZE_LIMITS.maxHeight}px`,
    );
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

export function scaleDimensionsToFit(dimensions: PageDimensions, maxWidth: number): PageDimensions {
  if (dimensions.width <= maxWidth) {
    return dimensions;
  }

  const scale = maxWidth / dimensions.width;
  return {
    width: maxWidth,
    height: Math.round(dimensions.height * scale),
  };
}
